import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Lightbulb, Plus, Edit, Trash2, ExternalLink, Calendar, X, Play, RefreshCw, Settings } from 'lucide-react';
import { IdeasService, Idea } from '../services/ideasService';
import { supabase } from '../lib/supabase';
import { generateThumbnailFromUrl, updateIdeaThumbnail } from '../utils/video/generateThumbnailFromUrl';
import { useStore } from '../store';
import { useArtistClient } from '../contexts/ArtistClientContext';
import { ArtistSettingsModal } from '../components/ArtistSettingsModal';

export const ArtistIdeas: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { artists, fetchArtists } = useStore();
  const { client: artistClient, isProtected, loading: clientLoading } = useArtistClient();
  
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const filterStatus = 'all';
  const filterFunnel = 'all';
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const backfillStartedRef = useRef(false);
  const analysisQueueRef = useRef(false);

  const backfillThumbnails = async (data: Idea[]) => {
    if (backfillStartedRef.current) return;
    backfillStartedRef.current = true;
    const missing = data.filter(i => !i.thumbnail_url && i.original_link?.includes('instagram.com'));
    console.log(`[thumbnails] ${missing.length} ideas missing thumbnails out of ${data.length} total`);
    if (!missing.length) return;
    const BATCH = 3;
    const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-instagram-thumbnail`;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    console.log('[thumbnails] calling', fnUrl);
    for (let i = 0; i < missing.length; i += BATCH) {
      const batch = missing.slice(i, i + BATCH);
      console.log(`[thumbnails] batch ${Math.floor(i / BATCH) + 1}: fetching`, batch.map(x => `${x.id} (${x.title})`));
      await Promise.allSettled(
        batch.map(idea =>
          fetch(fnUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${anonKey}`,
              'apikey': anonKey,
            },
            body: JSON.stringify({ url: idea.original_link, ideaId: idea.id }),
          })
            .then(r => r.json())
            .then(result => {
              const url = result?.thumbnailUrl;
              if (url) {
                console.log(`[thumbnails] ✅ ${idea.id} (${idea.title}):`, url.substring(0, 60));
                setIdeas(prev => prev.map(x => x.id === idea.id ? { ...x, thumbnail_url: url } : x));
              } else {
                console.warn(`[thumbnails] ⚠️ ${idea.id} (${idea.title}): no thumbnailUrl`, result);
              }
            })
            .catch(err => {
              console.warn(`[thumbnails] ❌ ${idea.id} (${idea.title}):`, err.message, err);
            })
        )
      );
      if (i + BATCH < missing.length) await new Promise(r => setTimeout(r, 1000));
    }
    console.log('[thumbnails] backfill complete');
  };

  const backfillVideoThumbnails = async (data: Idea[]) => {
    const missing = data.filter(i => !i.thumbnail_url && i.video_file_url);
    if (missing.length === 0) return;
    console.log(`[video-thumbnails] ${missing.length} ideas missing thumbnails from video files`);
    
    for (const idea of missing) {
      try {
        console.log(`[video-thumbnails] Generating thumbnail for ${idea.id} (${idea.title})`);
        const thumbnailUrl = await generateThumbnailFromUrl(idea.video_file_url, artistClient || undefined);
        if (thumbnailUrl) {
          await updateIdeaThumbnail(idea.id, thumbnailUrl, artistClient || undefined);
          setIdeas(prev => prev.map(x => x.id === idea.id ? { ...x, thumbnail_url: thumbnailUrl } : x));
          console.log(`[video-thumbnails] ✅ ${idea.id}: thumbnail generated`);
        } else {
          console.warn(`[video-thumbnails] ⚠️ ${idea.id}: failed to generate thumbnail`);
        }
      } catch (error) {
        console.error(`[video-thumbnails] ❌ ${idea.id}:`, error);
      }
      await new Promise(r => setTimeout(r, 500));
    }
    console.log('[video-thumbnails] backfill complete');
  };

  const backfillAnalysis = async (data: Idea[]) => {
    if (analysisQueueRef.current) return;
    analysisQueueRef.current = true;
    const queue = data.filter(i => !i.analysis_raw && i.original_link);
    console.log(`[analysis] ${queue.length} ideas need analysis out of ${data.length} total`);
    if (!queue.length) return;
    const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-video-twelvelabs`;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    for (const idea of queue) {
      console.log(`[analysis] processing ${idea.id} (${idea.title})`);
      try {
        const result = await fetch(fnUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ ideaId: idea.id }),
        }).then(r => r.json());
        if (result?.description) {
          console.log(`[analysis] ✅ ${idea.id} (${idea.title})`);
          setIdeas(prev => prev.map(x => x.id === idea.id ? { ...x, description: result.description, analysis_raw: result.analysis ?? true } : x));
        } else {
          console.warn(`[analysis] ⚠️ ${idea.id}: no description`, result);
        }
      } catch (err: any) {
        console.warn(`[analysis] ❌ ${idea.id}:`, err.message);
      }
    }
    console.log('[analysis] queue complete');
  };

  useEffect(() => {
    if (clientLoading || !id) return;
    fetchArtists(isProtected ? artistClient : undefined);
    loadIdeas().then(data => {
      if (!data) return;
      const ideaId = searchParams.get('idea');
      if (ideaId) {
        const found = data.find(i => i.id === ideaId);
        if (found) setSelectedIdea(found);
      }
      backfillThumbnails(data);
      backfillVideoThumbnails(data);
      backfillAnalysis(data);
    });
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [id, clientLoading, isProtected, artistClient, fetchArtists]);

  const openIdea = (idea: Idea) => {
    setSelectedIdea(idea);
    setSearchParams({ idea: idea.id }, { replace: true });
  };

  const closeIdea = () => {
    setSelectedIdea(null);
    setSearchParams({}, { replace: true });
  };

  const loadIdeas = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await IdeasService.getIdeas(id, artistClient || undefined);
      setIdeas(data);
      return data;
    } catch (error) {
      console.error('Error loading ideas:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (previousCount: number) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setIsPolling(true);
    let attempts = 0;
    pollingRef.current = setInterval(async () => {
      attempts++;
      const newIdeas = await IdeasService.getIdeas(id!, artistClient || undefined);
      setIdeas(newIdeas);
      if (newIdeas.length > previousCount || attempts >= 15) {
        clearInterval(pollingRef.current!);
        pollingRef.current = null;
        setIsPolling(false);
      }
    }, 3000);
  };

  const artist = artists.find(a => a.id === id);

  // Update document title with artist name
  useEffect(() => {
    if (artist) {
      document.title = `${artist.name} Ideas | SwipeUp Tool`;
    }
    return () => {
      document.title = 'SwipeOS';
    };
  }, [artist]);

  const filteredIdeas = ideas.filter(idea => {
    if (filterStatus !== 'all' && idea.status !== filterStatus) return false;
    if (filterFunnel !== 'all' && idea.funnel_level !== filterFunnel) return false;
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'unused':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      case 'used':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getFunnelLevelColor = (level: string | null) => {
    switch (level) {
      case 'tofu':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'mofu':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'bofu':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!artist && artists.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!artist && artists.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Artist not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please check the URL and try again
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-[#000000]">
      <header className="bg-primary-500 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col space-y-4">
            <div className="flex items-center space-x-2">
              <Lightbulb className="h-6 w-6" />
              <span className="text-xl font-bold">Ideas</span>
            </div>

            <div className="flex border-b border-white/20 justify-between">
              <div className="flex space-x-4">
                <Link
                  to={`/artist/${id}`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-5 w-5" fill="currentColor">
                      <path d="M96 160C96 124.7 124.7 96 160 96L480 96C515.3 96 544 124.7 544 160L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 160zM144 432L144 464C144 472.8 151.2 480 160 480L192 480C200.8 480 208 472.8 208 464L208 432C208 423.2 200.8 416 192 416L160 416C151.2 416 144 423.2 144 432zM448 416C439.2 416 432 423.2 432 432L432 464C432 472.8 439.2 480 448 480L480 480C488.8 480 496 472.8 496 464L496 432C496 423.2 488.8 416 480 416L448 416zM144 304L144 336C144 344.8 151.2 352 160 352L192 352C200.8 352 208 344.8 208 336L208 304C208 295.2 200.8 288 192 288L160 288C151.2 288 144 295.2 144 304zM448 288C439.2 288 432 295.2 432 304L432 336C432 344.8 439.2 352 448 352L480 352C488.8 352 496 344.8 496 336L496 304C496 295.2 488.8 288 480 288L448 288zM144 176L144 208C144 216.8 151.2 224 160 224L192 224C200.8 224 208 216.8 208 208L208 176C208 167.2 200.8 160 192 160L160 160C151.2 160 144 167.2 144 176zM448 160C439.2 160 432 167.2 432 176L432 208C432 216.8 439.2 224 448 224L480 224C488.8 224 496 216.8 496 208L496 176C496 167.2 488.8 160 480 160L448 160z"/>
                    </svg>
                    <span className="hidden sm:inline">Videos</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${id}/ad-creatives`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-5 w-5" fill="currentColor">
                      <path d="M544 96C526.3 96 512 110.3 512 128L512 137.1L126.8 247.2C123 233.8 110.6 224 96 224C78.3 224 64 238.3 64 256L64 384C64 401.7 78.3 416 96 416C110.6 416 123 406.2 126.8 392.8L198.5 413.3C194.3 424.1 192 435.8 192 448C192 501 235 544 288 544C334.9 544 374 510.3 382.3 465.8L512 502.8L512 511.9C512 529.6 526.3 543.9 544 543.9C561.7 543.9 576 529.6 576 511.9L576 127.9C576 110.2 561.7 95.9 544 95.9zM335.8 452.5C333.5 476.9 313 496 288 496C261.5 496 240 474.5 240 448C240 440.3 241.8 433 245 426.6L335.8 452.5z"/>
                    </svg>
                    <span className="hidden sm:inline">Ad Creatives</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${id}/content-plan`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5" />
                    <span className="hidden sm:inline">Content Plan</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${id}/ideas`}
                  className="pb-2 px-1 border-b-2 border-white text-white"
                >
                  <div className="flex items-center space-x-2">
                    <Lightbulb className="h-5 w-5" />
                    <span className="hidden sm:inline">Ideas</span>
                  </div>
                </Link>
              </div>

              <button
                onClick={() => setShowSettings(true)}
                className="text-white/70 hover:text-white transition-colors pb-2"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold dark:text-white">
                {artist?.name}'s Ideas
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Content ideas and concepts before they become video submissions
              </p>
            </div>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="btn flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>New Idea</span>
            </button>
          </div>

          {/* Processing banner */}
          {isPolling && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-3">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 flex-shrink-0"></div>
              <span className="text-sm text-blue-700 dark:text-blue-300">Processing idea... it will appear shortly.</span>
            </div>
          )}

          {/* Ideas Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : filteredIdeas.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
              <Lightbulb className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No ideas yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start collecting content ideas for your artist
              </p>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="btn"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create First Idea
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredIdeas.map((idea) => (
                <div key={idea.id} onClick={() => openIdea(idea)} className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow flex items-stretch overflow-hidden cursor-pointer">
                  {/* 9:16 thumbnail */}
                  <div className="flex-shrink-0 w-16" style={{ aspectRatio: '9/16' }}>
                    {idea.thumbnail_url ? (
                      <img
                        src={idea.thumbnail_url}
                        alt={idea.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <Lightbulb className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 px-4 py-3 flex flex-col justify-between min-w-0">
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {idea.title}
                        </h3>
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setEditingIdea(idea)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Edit"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this idea?')) {
                                IdeasService.deleteIdea(idea.id, artistClient || undefined).then(() => loadIdeas());
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {idea.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                          {idea.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 text-xs rounded ${getStatusColor(idea.status)}`}>
                        {idea.status.charAt(0).toUpperCase() + idea.status.slice(1)}
                      </span>
                      {idea.funnel_level && (
                        <span className={`px-1.5 py-0.5 text-xs rounded ${getFunnelLevelColor(idea.funnel_level)}`}>
                          {idea.funnel_level.toUpperCase()}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{formatDate(idea.created_at)}</span>
                      {idea.original_link && (
                        <a
                          href={idea.original_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 inline-flex items-center gap-0.5 ml-auto"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedIdea && (
          <IdeaDetailModal
            idea={selectedIdea}
            onClose={closeIdea}
            onEdit={() => { setEditingIdea(selectedIdea); closeIdea(); }}
            onDelete={() => {
              if (confirm('Are you sure you want to delete this idea?')) {
                IdeasService.deleteIdea(selectedIdea.id, artistClient || undefined).then(() => { loadIdeas(); closeIdea(); });
              }
            }}
            getStatusColor={getStatusColor}
            getFunnelLevelColor={getFunnelLevelColor}
            formatDate={formatDate}
          />
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || editingIdea) && (
          <IdeaModal
            artistId={id!}
            idea={editingIdea}
            existingLinks={ideas.map(i => i.original_link).filter(Boolean) as string[]}
            onClose={() => {
              setShowCreateModal(false);
              setEditingIdea(null);
            }}
            onSuccess={() => {
              setShowCreateModal(false);
              setEditingIdea(null);
              const currentCount = ideas.length;
              loadIdeas();
              startPolling(currentCount);
            }}
          />
        )}
      </main>

      <ArtistSettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
};

interface IdeaDetailModalProps {
  idea: Idea;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  getStatusColor: (status: string) => string;
  getFunnelLevelColor: (level: string) => string;
  formatDate: (date: string) => string;
}

const IdeaDetailModal: React.FC<IdeaDetailModalProps> = ({ idea, onClose, onEdit, onDelete, getStatusColor, getFunnelLevelColor, formatDate }) => {
  const isInstagram = idea.original_link?.includes('instagram.com');
  const isTikTok = idea.original_link?.includes('tiktok.com');
  const [description, setDescription] = useState<string | null>(idea.description);
  const [analyzing, setAnalyzing] = useState(false);
  // Video aspect ratio detected from metadata — lets the container
  // match the video's real dimensions instead of forcing 9/16.
  const [videoAspect, setVideoAspect] = useState<string>('9/16');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const runAnalysis = () => {
    setAnalyzing(true);
    const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-video-twelvelabs`;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    fetch(fnUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({ ideaId: idea.id }),
    })
      .then(r => r.json())
      .then(data => {
        if (data?.description) setDescription(data.description);
      })
      .catch(err => console.error('[analyze] error:', err))
      .finally(() => setAnalyzing(false));
  };

  useEffect(() => {
    if (!idea.analysis_raw) {
      runAnalysis();
    }
  }, [idea.id]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white line-clamp-1 flex-1 mr-4">{idea.title}</h2>
          <div className="flex items-center gap-2">
            <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Edit"><Edit className="h-4 w-4" /></button>
            <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="flex flex-row flex-1 overflow-hidden">
          {/* Video/Thumbnail + meta — left column */}
          <div className="flex-shrink-0 flex flex-col overflow-y-auto" style={{ width: '42%' }}>
            <div className="bg-black flex items-center justify-center p-3">
              <div className="relative w-full" style={{ aspectRatio: videoAspect, maxHeight: '60vh' }}>
                {idea.video_file_url ? (
                  <video
                    src={idea.video_file_url}
                    controls
                    autoPlay
                    poster={idea.thumbnail_url || undefined}
                    className="w-full h-full object-contain"
                    onLoadedMetadata={(e) => {
                      const v = e.currentTarget;
                      if (v.videoWidth && v.videoHeight) {
                        setVideoAspect(`${v.videoWidth}/${v.videoHeight}`);
                      }
                    }}
                  />
                ) : idea.thumbnail_url ? (
                  <>
                    <img src={idea.thumbnail_url} alt={idea.title} className="w-full h-full object-cover" />
                    {idea.original_link && (
                      <a
                        href={idea.original_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="h-6 w-6 text-gray-900 ml-1" />
                        </div>
                      </a>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center w-full h-full">
                    <Lightbulb className="h-12 w-12 text-gray-600" />
                  </div>
                )}
              </div>
            </div>

            {/* Meta panel below video */}
            <div className="p-4 space-y-3">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 text-xs rounded font-medium ${getStatusColor(idea.status)}`}>
                  {idea.status.charAt(0).toUpperCase() + idea.status.slice(1)}
                </span>
                {idea.funnel_level && (
                  <span className={`px-2 py-1 text-xs rounded font-medium ${getFunnelLevelColor(idea.funnel_level)}`}>
                    {idea.funnel_level.toUpperCase()}
                  </span>
                )}
                {(isInstagram || isTikTok) && (
                  <span className="px-2 py-1 text-xs rounded font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                    {isInstagram ? 'Instagram' : 'TikTok'}
                  </span>
                )}
              </div>

              {/* Meta */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Calendar className="h-4 w-4 flex-shrink-0" />
                  <span>{formatDate(idea.created_at)}</span>
                </div>
                {idea.original_link && (
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 flex-shrink-0 text-gray-500" />
                    <a
                      href={idea.original_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 truncate"
                    >
                      {idea.original_link}
                    </a>
                  </div>
                )}
              </div>

              {/* Open original */}
              {idea.original_link && (
                <a
                  href={idea.original_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-700 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 text-sm font-medium rounded-lg transition-colors w-full justify-center"
                >
                  <Play className="h-4 w-4" />
                  Open {isInstagram ? 'on Instagram' : isTikTok ? 'on TikTok' : 'Original'}
                </a>
              )}

              {/* Re-analyze */}
              <button
                onClick={runAnalysis}
                disabled={analyzing}
                className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600 disabled:opacity-50"
                title="Re-analyze with TwelveLabs"
              >
                <RefreshCw className={`h-3 w-3 ${analyzing ? 'animate-spin' : ''}`} />
                {analyzing ? 'Analyzing...' : 'Re-analyze'}
              </button>
            </div>
          </div>

          {/* Analysis panel — right column */}
          <div className="flex-1 p-5 overflow-y-auto">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Analysis</p>
            <div className="max-h-full overflow-y-auto pr-2">
              {analyzing ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-primary-500 flex-shrink-0" />
                  Analyzing video...
                </div>
              ) : description ? (
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{description}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">No analysis yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface IdeaModalProps {
  artistId: string;
  idea: Idea | null;
  existingLinks: string[];
  onClose: () => void;
  onSuccess: () => void;
}

const IdeaModal: React.FC<IdeaModalProps> = ({
  artistId,
  idea,
  existingLinks,
  onClose,
  onSuccess
}) => {
  const [title, setTitle] = useState(idea?.title || '');
  const [description, setDescription] = useState(idea?.description || '');
  const [originalLink, setOriginalLink] = useState(idea?.original_link || '');
  const [thumbnailUrl, setThumbnailUrl] = useState(idea?.thumbnail_url || '');
  const [videoFileUrl, setVideoFileUrl] = useState(idea?.video_file_url || '');
  const [funnelLevel, setFunnelLevel] = useState(idea?.funnel_level || '');
  const [contentType, setContentType] = useState(idea?.content_type || '');
  const [saving, setSaving] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const isDuplicate = !idea && existingLinks.includes(originalLink.trim());

  const fetchInstagramData = async (url: string) => {
    if (!url || !url.includes('instagram.com')) return;
    
    try {
      setFetchingData(true);

      const { data: result } = await supabase.functions.invoke('fetch-instagram-data', {
        body: { url },
      });
      
      if (result?.success && result.data) {
        if (!title.trim()) {
          setTitle(result.data.title || '');
        }
        if (!description.trim()) {
          setDescription(result.data.description || '');
        }
        setThumbnailUrl(result.data.thumbnailUrl || '');
        setVideoFileUrl(result.data.videoUrl || '');
      }
    } catch (error) {
      console.error('Error fetching Instagram data:', error);
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (originalLink && originalLink.includes('instagram.com')) {
        fetchInstagramData(originalLink);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [originalLink]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!originalLink.trim()) return;

    try {
      setSaving(true);
      
      const { data: result, error: invokeError } = await supabase.functions.invoke('add-idea', {
        body: {
          sourceUrl: originalLink.trim(),
          artistId,
          funnelLevel: funnelLevel || null,
          contentType: contentType || null,
          title: title.trim() || null,
          description: description.trim() || null,
          thumbnailUrl: thumbnailUrl || null,
          videoFileUrl: videoFileUrl || null,
        },
      });

      if (invokeError) throw invokeError;
      
      if (result?.success) {
        onSuccess();
      } else {
        throw new Error(result.error || 'Failed to create idea');
      }
    } catch (error) {
      console.error('Error saving idea:', error);
      alert('Failed to save idea. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {idea ? 'Edit Idea' : 'Create New Idea'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Original Link *
              </label>
              <div className="relative">
                <input
                  type="url"
                  value={originalLink}
                  onChange={(e) => setOriginalLink(e.target.value)}
                  placeholder="https://instagram.com/p/..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                  required
                />
                {fetchingData && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-500"></div>
                  </div>
                )}
              </div>
              {isDuplicate && (
                <p className="text-xs text-red-500 mt-1">This URL has already been added for this artist.</p>
              )}
              {originalLink && originalLink.includes('instagram.com') && !fetchingData && !isDuplicate && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  ✓ Instagram data fetched
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter idea title..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {(description || thumbnailUrl) && (
              <div className="flex gap-4">
                {thumbnailUrl && (
                  <div className="w-32 h-32 flex-shrink-0">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="w-full h-full object-cover rounded-md border border-gray-300 dark:border-gray-600"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your idea..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Funnel Level
                </label>
                <select
                  value={funnelLevel}
                  onChange={(e) => setFunnelLevel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select level</option>
                  <option value="tofu">TOFU</option>
                  <option value="mofu">MOFU</option>
                  <option value="bofu">BOFU</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Content Type
                </label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select type</option>
                  <option value="song_specific">Song-specific</option>
                  <option value="off_topic">Off-topic</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !originalLink.trim() || isDuplicate}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : (idea ? 'Update Idea' : 'Create Idea')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
